import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as Sentry from '@sentry/nextjs'
import useAccessToken from '../useAccessToken'
import useApi from '../useApi'

const TRUNCATED_RETRY_INTERVAL_MS = 30000

export default function useTodoCompletionNotifications({
  organizationId,
  customerId,
  todoIds,
  enabled = true,
}) {
  const client = useQueryClient()
  const { getAccessToken, isSignedIn } = useAccessToken()
  const {
    getTodoNotificationSubscriptions,
    updateTodoNotificationSubscription,
  } = useApi({ getAccessToken })
  const queryKey = [
    'todoCompletionNotifications',
    { organizationId, customerId },
  ]
  const subscriptions = useQuery(
    queryKey,
    () =>
      getTodoNotificationSubscriptions({
        organizationId,
        customerId,
        todoIds,
      }),
    {
      enabled:
        enabled &&
        isSignedIn &&
        Boolean(organizationId) &&
        Boolean(customerId) &&
        todoIds.length > 0,
      staleTime: 30000,
      refetchInterval: (data) =>
        data?.truncated ? TRUNCATED_RETRY_INTERVAL_MS : false,
      onError: (error) => Sentry.captureException(error),
    },
  )
  const updateSubscription = useMutation(
    ({ todoId, subscribed }) =>
      updateTodoNotificationSubscription({
        organizationId,
        customerId,
        todoId,
        subscribed,
      }),
    {
      onMutate: async ({ todoId, subscribed }) => {
        await client.cancelQueries(queryKey)
        const previous = client.getQueryData(queryKey)

        client.setQueryData(queryKey, (current) => {
          const currentIds = new Set(current?.subscribedTodoIds || [])

          if (subscribed) {
            currentIds.add(String(todoId))
          } else {
            currentIds.delete(String(todoId))
          }

          return {
            ...(current || {}),
            configured: true,
            subscribedTodoIds: [...currentIds],
          }
        })

        return { previous }
      },
      onError: (error, _, context) => {
        client.setQueryData(queryKey, context?.previous)
        Sentry.captureException(error)
      },
      onSettled: () => client.invalidateQueries(queryKey),
    },
  )
  const subscribedTodoIds = new Set(subscriptions.data?.subscribedTodoIds || [])
  const coveredTodoIds = new Set(subscriptions.data?.coveredTodoIds || [])

  return {
    configured: subscriptions.data?.configured === true,
    isTruncated: subscriptions.data?.truncated === true,
    isLoading: subscriptions.isLoading,
    errorTodoId: updateSubscription.isError
      ? String(updateSubscription.variables?.todoId)
      : null,
    pendingTodoId: updateSubscription.isLoading
      ? String(updateSubscription.variables?.todoId)
      : null,
    isSubscribed: (todoId) => subscribedTodoIds.has(String(todoId)),
    isUnavailable: (todoId) =>
      subscriptions.data?.truncated === true &&
      !coveredTodoIds.has(String(todoId)),
    setSubscribed: (todoId, subscribed) =>
      updateSubscription.mutate({ todoId, subscribed }),
  }
}
